/* Admin dashboard primitives — read-only, no mutations. */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/hooks/useAuth";

export const INK = "#1C1C1C";
export const INDIGO = "#5D00FF";
export const AQUA = "#00C2CB";
export const MUTE = "#676773";
export const BORDER = "#E8E6EF";

export function PageHeader({ title, subtitle, right }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between",
                   alignItems: "flex-end", flexWrap: "wrap", gap: 12,
                   marginBottom: 20 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: INK, margin: 0 }}>{title}</h1>
        {subtitle && <div style={{ fontSize: 12, color: MUTE, marginTop: 4 }}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

export function KpiGrid({ children }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
      gap: 12, marginBottom: 20,
    }}>
      {children}
    </div>
  );
}

export function KpiCard({ label, value, hint, testId }) {
  return (
    <div data-testid={testId} style={{
      background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
      padding: "16px 18px", minWidth: 0,
    }}>
      <div style={{ fontSize: 10.5, letterSpacing: "0.18em",
                     color: MUTE, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color: INK,
                     marginTop: 6, fontVariantNumeric: "tabular-nums",
                     overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value == null ? "—" : (typeof value === "number" ? value.toLocaleString() : value)}
      </div>
      {hint != null && (
        <div style={{ fontSize: 11, color: MUTE, marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

export function Panel({ title, right, children, testId }) {
  return (
    <section data-testid={testId} style={{
      background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
      padding: 18, marginBottom: 18,
    }}>
      {(title || right) && (
        <div style={{ display: "flex", justifyContent: "space-between",
                       alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: INK,
                        letterSpacing: "0.08em", textTransform: "uppercase",
                        margin: 0 }}>{title}</h3>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function Table({ cols, rows, empty = "No data", keyFn, testId, stackOnMobile = true }) {
  return (
    <div className={stackOnMobile ? "admin-table-wrap admin-stack-sm" : "admin-table-wrap"}
         style={{ overflowX: "auto", maxWidth: "100%", minWidth: 0,
                   WebkitOverflowScrolling: "touch" }}
         data-testid={testId}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ color: MUTE, textAlign: "left",
                        fontSize: 10.5, textTransform: "uppercase",
                        letterSpacing: "0.14em" }}>
            {cols.map((c, i) => <th key={i} style={{ padding: "8px 10px",
                                                       fontWeight: 500,
                                                       whiteSpace: "nowrap" }}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} style={{ padding: 18,
                       textAlign: "center", color: MUTE }}>{empty}</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={keyFn ? keyFn(r) : i}
                style={{ borderTop: `1px solid ${BORDER}`, color: INK }}>
              {cols.map((c, j) => (
                <td key={j}
                    data-label={c.label}
                    style={{ padding: "10px 10px", whiteSpace: "nowrap",
                             overflow: "hidden", textOverflow: "ellipsis",
                             maxWidth: 360 }}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Mobile stacked-card variant: table collapses into a labelled
          list under 640px so the overall page never leaks horizontally.
          Narrow tables (<=3 cols) keep the native layout. */}
      <style>{`
        @media (max-width: 640px) {
          .admin-stack-sm { overflow-x: visible; }
          .admin-stack-sm table, .admin-stack-sm thead,
          .admin-stack-sm tbody, .admin-stack-sm tr,
          .admin-stack-sm th,    .admin-stack-sm td { display: block; width: 100%; }
          .admin-stack-sm thead { display: none; }
          .admin-stack-sm tr {
            border: 1px solid ${BORDER}; border-radius: 12px;
            padding: 10px 12px; margin-bottom: 10px; background: white;
          }
          .admin-stack-sm td {
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: clip !important;
            max-width: none !important;
            padding: 6px 0 !important;
            display: flex; justify-content: space-between; gap: 12px;
            border-top: 1px dashed ${BORDER} !important;
          }
          .admin-stack-sm tr td:first-child { border-top: 0 !important; }
          .admin-stack-sm td::before {
            content: attr(data-label);
            font-size: 10px; letter-spacing: 0.14em; color: ${MUTE};
            text-transform: uppercase; flex: 0 0 auto;
            align-self: center;
          }
          .admin-stack-sm td > * {
            text-align: right; min-width: 0;
            overflow-wrap: anywhere; word-break: break-word;
          }
        }
      `}</style>
    </div>
  );
}

export function Pager({ page, pageSize, total, onPage }) {
  const last = Math.max(1, Math.ceil((total || 0) / pageSize));
  return (
    <div style={{ display: "flex", justifyContent: "space-between",
                   alignItems: "center", marginTop: 14,
                   fontSize: 12, color: MUTE }}>
      <div>Page {page} of {last} · {total.toLocaleString()} records</div>
      <div style={{ display: "flex", gap: 6 }}>
        <PagerBtn disabled={page <= 1}    onClick={() => onPage(page - 1)}>Prev</PagerBtn>
        <PagerBtn disabled={page >= last} onClick={() => onPage(page + 1)}>Next</PagerBtn>
      </div>
    </div>
  );
}

function PagerBtn({ children, ...rest }) {
  return (
    <button {...rest} style={{
      padding: "6px 12px", borderRadius: 8, fontSize: 12,
      background: "white", border: `1px solid ${BORDER}`,
      color: INK, cursor: rest.disabled ? "not-allowed" : "pointer",
      opacity: rest.disabled ? 0.45 : 1,
    }}>{children}</button>
  );
}

export function StatusChip({ status }) {
  const s = String(status || "").toUpperCase();
  const color = s === "COMPLETED" || s === "HEALTHY" || s === "READY" ? "#15803D"
            : s === "FAILED"    || s === "DOWN"      || s === "ERROR" ? "#B91C1C"
            : s === "GENERATING" || s === "QUEUED"  || s === "RENDERING"
              || s === "VALIDATING" || s === "CREDITS_RESERVED"       ? INDIGO
            : s === "REFUNDED"                                        ? "#A16207"
            : MUTE;
  return (
    <span style={{
      fontSize: 10.5, padding: "2px 8px", borderRadius: 999,
      background: color + "15", color,
      fontWeight: 500, letterSpacing: "0.06em",
    }}>{s || "—"}</span>
  );
}

export function useAdminQuery(url, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  useEffect(() => {
    let dead = false;
    setLoading(true);
    api.get(url, { params })
      .then((r) => { if (!dead) { setData(r.data); setError(null); } })
      .catch((e) => { if (!dead) { setError(e); setData(null); } })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, JSON.stringify(params || {}), refreshKey]);
  return { data, loading, error, refresh };
}

export function RefreshButton({ onClick, loading }) {
  return (
    <button onClick={onClick} disabled={loading} data-testid="admin-refresh"
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", fontSize: 12, borderRadius: 999,
        border: `1px solid ${BORDER}`, background: "white", color: INK,
        cursor: loading ? "wait" : "pointer",
      }}>
      {loading ? "Refreshing…" : "Refresh"}
    </button>
  );
}

export function Unavailable({ error }) {
  return (
    <div data-testid="admin-unavailable" style={{
      padding: 20, border: `1px dashed ${BORDER}`, borderRadius: 12,
      color: MUTE, fontSize: 13, textAlign: "center",
      background: "rgba(185,28,28,0.04)",
    }}>
      Data unavailable. {error?.response?.status === 403
        ? "You lack admin permission."
        : error?.message || "Backend unreachable."}
    </div>
  );
}

export function fmtDate(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, { year: "numeric", month: "short",
      day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return String(ts); }
}

export function fmtDuration(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
